import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Pencil, ShieldAlert } from 'lucide-react';
import { StatutoryOverrideRow } from './StatutoryOverrideRow';
import { ApplyScopePrompt } from './ApplyScopePrompt';
import { STATUTORY_FIELDS, EARNING_FIELDS, DEDUCTION_FIELDS, currentComponents, fmtRM } from './payslipEditUtils';

const toStr = (n) => (Number(n || 0)).toFixed(2);

function FieldGroup({ title, fields, form, onChange, tone }) {
  return (
    <div className={`rounded-lg p-4 ${tone}`}>
      <h4 className="font-semibold text-sm mb-3">{title}</h4>
      <div className="space-y-2">
        {fields.map((f) => (
          <div key={f.key} className="grid grid-cols-12 items-center gap-2">
            <Label className="col-span-7 text-xs sm:text-sm font-normal">{f.label}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form[f.key] ?? ''}
              onChange={(e) => onChange(f.key, e.target.value)}
              data-testid={`edit-field-${f.key}`}
              className="col-span-5 h-8 text-right text-sm"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function EditPayslipDialog({ open, onOpenChange, payslip, onSaved }) {
  const initial = useMemo(() => currentComponents(payslip), [payslip]);
  const [form, setForm] = useState({});
  const [overrides, setOverrides] = useState({});
  const [overrideReason, setOverrideReason] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [saving, setSaving] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);

  useEffect(() => {
    if (!open || !payslip) return;
    const f = {};
    Object.keys(initial).forEach((k) => { f[k] = toStr(initial[k]); });
    setForm(f);
    const ov = {};
    STATUTORY_FIELDS.forEach(({ key }) => {
      const meta = payslip.statutory_overrides?.[key];
      ov[key] = meta && meta.value != null ? { enabled: true, value: toStr(meta.value) } : { enabled: false, value: '' };
    });
    setOverrides(ov);
    setOverrideReason(Object.values(payslip.statutory_overrides || {})[0]?.reason || '');
    setNote('');
    setPreview(null);
    setPreviewError('');
  }, [open, payslip, initial]);

  const buildPayload = (dryRun) => {
    const p = { dry_run: dryRun, statutory_overrides: {} };
    [...EARNING_FIELDS, ...DEDUCTION_FIELDS].forEach(({ key }) => { p[key] = Number(form[key] || 0); });
    STATUTORY_FIELDS.forEach(({ key }) => {
      const ov = overrides[key];
      const had = payslip?.statutory_overrides?.[key]?.value != null;
      if (ov?.enabled) p.statutory_overrides[key] = Number(ov.value || 0);
      else if (had) p.statutory_overrides[key] = null;
    });
    if (Object.keys(p.statutory_overrides).length === 0) delete p.statutory_overrides;
    p.override_reason = overrideReason.trim() || (dryRun ? 'preview' : '');
    p.reason = note.trim();
    return p;
  };

  useEffect(() => {
    if (!open || !payslip?.payslip_id || Object.keys(form).length === 0) return undefined;
    const t = setTimeout(async () => {
      try {
        const res = await axios.put(`${API}/payslips/${payslip.payslip_id}/full-edit`, buildPayload(true));
        setPreview(res.data);
        setPreviewError('');
      } catch (err) {
        setPreview(null);
        setPreviewError(err.response?.data?.detail || 'Could not calculate preview');
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form, overrides, open, payslip?.payslip_id]);

  const anyOverride = Object.values(overrides).some((o) => o?.enabled);
  const changedProfileFields = [...EARNING_FIELDS, ...DEDUCTION_FIELDS]
    .filter((f) => f.profile && Math.abs(Number(form[f.key] || 0) - Number(initial[f.key] || 0)) > 0.004)
    .map((f) => f.label);
  const isProrated = payslip?.prorated && Number(payslip?.proration_factor || 1) !== 1;

  const save = async (applyToProfile) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/payslips/${payslip.payslip_id}/full-edit`, { ...buildPayload(false), apply_to_profile: applyToProfile });
      const n = res.data?.changes?.length || 0;
      toast.success(`Payslip updated (${n} change${n === 1 ? '' : 's'})${res.data?.profile_updated?.length ? ' · employee profile updated' : ''}`);
      setScopeOpen(false);
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payslip');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (anyOverride && !overrideReason.trim()) {
      toast.error('Please provide a reason for the statutory override(s)');
      return;
    }
    if (previewError) {
      toast.error(previewError);
      return;
    }
    if (changedProfileFields.length > 0 && !isProrated) setScopeOpen(true);
    else save(false);
  };

  const p = preview?.preview;
  const delta = preview?.net_delta ?? 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto dark:bg-slate-800" data-testid="edit-payslip-dialog">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
              <Pencil className="h-5 w-5 text-emerald-500" />
              Edit Payslip — {payslip?.employee?.full_name || payslip?.employee_name || payslip?.employee_id}
              <span className="text-sm font-normal text-slate-500 ml-1">
                {payslip ? new Date(payslip.year, payslip.month - 1, 1).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' }) : ''}
              </span>
            </DialogTitle>
          </DialogHeader>

          {isProrated && (
            <p className="text-xs rounded-md bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 px-3 py-2" data-testid="edit-prorated-note">
              This payslip is prorated (factor {Number(payslip.proration_factor).toFixed(4)}). Changes apply to this payslip only; update the employee profile from the Employees page.
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldGroup title="Earnings" fields={EARNING_FIELDS} form={form} tone="bg-slate-50 dark:bg-slate-900"
              onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))} />
            <div className="space-y-4">
              <FieldGroup title="Deductions" fields={DEDUCTION_FIELDS} form={form} tone="bg-red-50 dark:bg-red-900/20"
                onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))} />
              {Number(payslip?.unpaid_leave_deduction || 0) > 0 && (
                <p className="text-xs text-slate-500 px-1">Unpaid leave ({fmtRM(payslip.unpaid_leave_deduction)}) is system-computed from approved leave and kept as-is.</p>
              )}
            </div>
          </div>

          <div className="rounded-lg p-4 bg-amber-50/60 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-sm text-amber-900 dark:text-amber-200 flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" /> Statutory contributions
              </h4>
              <span className="text-[11px] text-amber-800 dark:text-amber-300">Tick to override the auto value</span>
            </div>
            <div className="divide-y divide-amber-200/60 dark:divide-amber-800/40">
              {STATUTORY_FIELDS.map((f) => (
                <StatutoryOverrideRow
                  key={f.key}
                  field={f}
                  autoValue={preview?.auto_statutory?.[f.key]}
                  override={overrides[f.key]}
                  onToggle={(k, on) => setOverrides((o) => ({ ...o, [k]: { enabled: on, value: on ? (o[k]?.value || toStr(preview?.auto_statutory?.[k])) : '' } }))}
                  onChange={(k, v) => setOverrides((o) => ({ ...o, [k]: { ...o[k], value: v } }))}
                />
              ))}
            </div>
            {anyOverride && (
              <div className="mt-3">
                <Label className="text-xs text-amber-900 dark:text-amber-200">Override reason <span className="text-rose-600">*</span></Label>
                <Input
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="e.g. KWSP directive letter ref 123 — voluntary 9% rate"
                  data-testid="override-reason-input"
                  className="mt-1 h-9 text-sm border-amber-400"
                />
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs text-slate-600 dark:text-slate-400">Edit note (optional, shown in audit trail)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="edit-note-input" className="mt-1 text-sm" placeholder="Why is this payslip being changed?" />
          </div>

          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="edit-preview">
            <div><p className="text-xs text-slate-500">Gross</p><p className="font-semibold tabular-nums">{p ? fmtRM(p.gross_salary) : '…'}</p></div>
            <div><p className="text-xs text-slate-500">Total Deductions</p><p className="font-semibold tabular-nums text-rose-700 dark:text-rose-400">{p ? fmtRM(p.total_deductions) : '…'}</p></div>
            <div><p className="text-xs text-slate-500">Net Salary</p><p className="font-bold tabular-nums text-emerald-700 dark:text-emerald-400" data-testid="edit-preview-net">{p ? fmtRM(p.net_salary) : '…'}</p></div>
            <div>
              <p className="text-xs text-slate-500">Change vs current</p>
              <p className={`font-semibold tabular-nums ${delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-rose-700' : 'text-slate-500'}`} data-testid="edit-preview-delta">
                {p ? `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${fmtRM(Math.abs(delta))}` : '…'}
              </p>
            </div>
            {previewError && <p className="col-span-full text-xs text-rose-600" data-testid="edit-preview-error">{previewError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="edit-payslip-cancel">Cancel</Button>
            <Button onClick={handleSaveClick} disabled={saving || !preview} data-testid="edit-payslip-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ApplyScopePrompt
        open={scopeOpen}
        onOpenChange={setScopeOpen}
        fields={changedProfileFields}
        employeeName={payslip?.employee?.full_name || payslip?.employee_name}
        saving={saving}
        onChoose={save}
      />
    </>
  );
}
